import { Box, Divider, IconButton, Tooltip, Typography } from "@mui/material"
import { ListContextProvider, useListContext } from "ra-core"
import { FC } from "react"
import { Link as RouterLink } from 'react-router-dom'
import { RecPatch } from "@/dataProvider/tools"
import { EncryptionMappingResponseDto } from "@/gen/api/governance"
import ArrowRightIcon from '@/icons/ArrowRight';
import { genUrlWithHashid } from "@/utils/hashid"
import DatabaseNameField from "../databases/DatabaseNameField"
import { FunctionField } from "../field"
import SizeTextField from "../field/SizeTextField"
import { Datagrid } from "../list/datagrid"
import { AtopFuncArea, SearchField } from "../list/datagridFunctionWidgets"
import useLocalListController from "../list/useLocalListController"
import Scrollbar from "../Scrollbar"
import { Pagination as DefaultPagination } from '@/components/list/pagination'
import { nonSqlDbTypes } from "./defs"


const databaseListOptions : {sorters: Record<string, (a:EncryptionMappingResponseDto & RecPatch, b:EncryptionMappingResponseDto & RecPatch) => number>} = {
  sorters: {
    id: (a, b) => a.id < b.id ? -1 : 1,
    name: (a, b) => (a.databaseName || '') < (b.databaseName || '') ? -1 : 1,
    sizeText: (a, b) => (a.size || 0) < (b.size || 0) ? -1 : 1,
  }
}
const databaseListPersistFilter = {
  excludeNoSql: (x: EncryptionMappingResponseDto) => !nonSqlDbTypes.has(x.databaseType!)
}

const DbSubsettingList: FC = () => {
  const listCtrl = useLocalListController({ resource: 'encryption/mappings', basePath: '' }, databaseListOptions)
  return (
    <ListContextProvider value={listCtrl}>
      <DbSubsettingListBody />
    </ListContextProvider>
  )
}


const DbSubsettingListBody: FC = () => {
  const listCtx = useListContext()
  const { data, ids } = listCtx

  return (
    <Box >
      <AtopFuncArea persistFilters={databaseListPersistFilter}>
        <SearchField 
          sourceKey="dbName"
          match={(d: EncryptionMappingResponseDto, fval:string) => !!d.databaseName && d.databaseName.toLowerCase().includes(fval.trim().toLowerCase())}
          sx={{ width: 280 }}
          TextFieldProps={{ placeholder: 'Search by Database Name' }}
        />
      </AtopFuncArea>
      <Divider sx={{ pb: 0 }} />
      <Scrollbar options={{ suppressScrollY: true }}>
        <Box sx={{ minWidth: 1150 }}>
          <Datagrid
            hasBulkActions={false} 
            {...listCtx}
            sx={{ 
              '& th' : { py: 2 },
              '& td' : { py: 2 },
              '& tr div.MuiBox-root': {
                '& div.encryptedDbTip': { top: theme => theme.spacing(-3.25) },
                '& span.encryptedDbTipText': { top: theme => theme.spacing(-3.25) },
              },
            }}
          >
            <DatabaseNameField label="Database" keyMap={{ name: 'databaseName', }} />
            <FunctionField 
              source="engineVersion" 
              label="Engine" 
              sortable={false} 
              render={(d?: EncryptionMappingResponseDto & RecPatch) => (
                <Typography
                  component="p"
                  variant="body2"
                >
                  {d?.engineVersion + ' ' + (d?.engineEdition || '')}
                </Typography>
              )}
            />
            <SizeTextField source="sizeText" numericSource="size" label="Size" emptyText="N/A" />
            <FunctionField 
              label="Action"
              sortable={false}
              render={(d?: EncryptionMappingResponseDto & RecPatch) => (
                <Tooltip title="Details">
                  <IconButton 
                    sx={{ 
                      color: 'text.primary' as any, 
                      '&:hover': { backgroundColor: 'action.hover' },
                    }}
                    component={RouterLink}
                    to={genUrlWithHashid(`/encryption/subsetting/mastertables/$0/show`, d?.databaseId || 0, d?.databaseName)}
                  >
                    <ArrowRightIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )} 
            />
          </Datagrid>
          {//@ts-ignore 
          <DefaultPagination {...listCtx} />}
        </Box>
      </Scrollbar>
    </Box>
  )
}

export default DbSubsettingList;
